import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';

// Maps integration target routes to sidebar item IDs
const ROUTE_TO_SIDEBAR: Record<string, string> = {
  '/dashboard/chat': 'chat',
  '/dashboard/kalender': 'calendar',
  '/dashboard/daten': 'data',
  '/dashboard/marketing': 'marketing',
  '/dashboard/kontakte': 'contacts',
  '/dashboard/email': 'email',
  '/dashboard/team': 'team',
  '/dashboard/finanzen': 'finanzen',
};

export interface IntegrationNotification {
  sidebarId: string;
  integrationName: string;
  connectedAt: string;
}

export const useIntegrationNotifications = () => {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();

  // Fetch recently connected integrations that haven't been "seen" yet
  const { data: notifications = [] } = useQuery({
    queryKey: ['integration-notifications', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      // Get last viewed timestamp for integrations
      const { data: viewedData } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', `integration_notifications_viewed_${user.id}`)
        .is('project_id', null)
        .maybeSingle();
      
      const lastViewed = (viewedData?.setting_value as { timestamp: string })?.timestamp || '2000-01-01';
      
      // Get integrations connected after last viewed
      const { data: integrations } = await supabase
        .from('integrations')
        .select('name, connected_at, config')
        .eq('status', 'connected')
        .gt('connected_at', lastViewed)
        .order('connected_at', { ascending: false });

      if (!integrations?.length) return [];

      const result: IntegrationNotification[] = [];
      for (const int of integrations) {
        const config = int.config as Record<string, any> | null;
        const targetRoute = config?.targetRoute as string;
        if (targetRoute && ROUTE_TO_SIDEBAR[targetRoute]) {
          result.push({
            sidebarId: ROUTE_TO_SIDEBAR[targetRoute],
            integrationName: int.name,
            connectedAt: int.connected_at || '',
          });
        }
      }
      return result;
    },
    enabled: !!user,
  });

  // Mark notifications as seen for a specific sidebar item
  const markIntegrationSeen = async (sidebarId: string) => {
    if (!user) return;
    const key = `integration_notifications_viewed_${user.id}`;
    const now = new Date().toISOString();
    try {
      // Check if row exists first
      const { data: existing } = await supabase
        .from('system_settings')
        .select('id')
        .eq('setting_key', key)
        .is('project_id', null)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('system_settings')
          .update({ setting_value: { timestamp: now }, updated_at: now })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('system_settings')
          .insert({ setting_key: key, project_id: null, setting_value: { timestamp: now }, updated_at: now });
      }
      queryClient.invalidateQueries({ queryKey: ['integration-notifications'] });
    } catch (e) {
      // Silently ignore - non-critical
    }
  };

  // Get count of notifications for a specific sidebar item
  const getNotificationCount = (sidebarId: string) => {
    return notifications.filter(n => n.sidebarId === sidebarId).length;
  };

  // Get notification text for a sidebar item
  const getNotificationText = (sidebarId: string) => {
    const items = notifications.filter(n => n.sidebarId === sidebarId);
    if (items.length === 0) return null;
    return `${items.length} neue Integration${items.length > 1 ? 'en' : ''}`;
  };

  return {
    notifications,
    getNotificationCount,
    getNotificationText,
    markIntegrationSeen,
  };
};
